<script setup>
/* global axios */
import { onBeforeMount, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  useOvvaBackend,
  getOvvaPat,
  setOvvaPat,
} from 'dashboard/composables/useOvvaBackend';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const { callBackend } = useOvvaBackend();
const { accountId } = useAccount();

const SENDER_PLACEHOLDER = 'OVVA <noreply@example.com>';

// useOvvaBackend() кладёт код ответа в error.status: 401 — embed-токен панели,
// 403 — RolesGuard / BlockEmbedTokenGuard (личный Access Token не задан или
// не подошёл). Обе причины лечатся возвратом к вводу токена.
const isAuthRejected = error => error?.status === 401 || error?.status === 403;

const SECRET_FIELDS = ['wazzupApiKey', 'telegramBotToken', 'maxApiKey'];
const PLAIN_FIELDS = ['wazzupChannelId'];

const MAIL_PLAIN_FIELDS = {
  smtpAddress: 'smtp_address',
  smtpPort: 'smtp_port',
  smtpUsername: 'smtp_username',
  smtpSenderEmail: 'smtp_sender_email',
};

const showAuthGate = ref(!getOvvaPat());
const patInputValue = ref('');
const patError = ref('');

const isFetching = ref(false);
const isSaving = ref(false);

// Ответ GET /system/integrations: секреты приходят замаскированными и
// служат плейсхолдерами, обычные поля — текущими значениями.
const current = ref(null);

const form = reactive({
  wazzupApiKey: '',
  wazzupChannelId: '',
  telegramBotToken: '',
  maxApiKey: '',
});

const isFetchingMail = ref(false);
const isSavingMail = ref(false);
const mailCurrent = ref(null);
const mailDeniedMessage = ref('');

const mailForm = reactive({
  smtpAddress: '',
  smtpPort: '',
  smtpUsername: '',
  smtpPassword: '',
  smtpSenderEmail: '',
});

const applyToForm = data => {
  SECRET_FIELDS.forEach(key => {
    form[key] = '';
  });
  PLAIN_FIELDS.forEach(key => {
    form[key] = data?.[key] ? String(data[key]) : '';
  });
};

const maskFor = key => current.value?.[key] || '';

const resetToGate = message => {
  current.value = null;
  showAuthGate.value = true;
  patError.value = message || '';
};

const fetchIntegrations = async () => {
  isFetching.value = true;
  try {
    const data = await callBackend('/system/integrations');
    current.value = data;
    applyToForm(data);
  } catch (error) {
    if (isAuthRejected(error)) {
      resetToGate(error.message);
    } else {
      useAlert(error.message || t('OVVA_INTEGRATIONS_MGMT.API.FETCH_ERROR'));
    }
  } finally {
    isFetching.value = false;
  }
};

const submitPat = () => {
  const value = patInputValue.value.trim();
  if (value.length < 10) {
    patError.value = t('OVVA_INTEGRATIONS_MGMT.PAT.TOO_SHORT');
    return;
  }
  setOvvaPat(value);
  patInputValue.value = '';
  patError.value = '';
  showAuthGate.value = false;
  fetchIntegrations();
};

const disconnectPat = () => {
  setOvvaPat('');
  current.value = null;
  showAuthGate.value = true;
  patError.value = '';
};

// Отправляем только заполненные секреты и только изменённые обычные поля.
const buildPatch = () => {
  const body = {};
  SECRET_FIELDS.forEach(key => {
    const value = String(form[key]).trim();
    if (value) body[key] = value;
  });
  PLAIN_FIELDS.forEach(key => {
    const value = String(form[key]).trim();
    const saved = String(current.value?.[key] ?? '');
    if (value && value !== saved) body[key] = value;
  });
  return body;
};

const saveSettings = async () => {
  const body = buildPatch();
  if (!Object.keys(body).length) {
    useAlert(t('OVVA_INTEGRATIONS_MGMT.API.NOTHING_TO_SAVE'));
    return;
  }
  isSaving.value = true;
  try {
    const result = await callBackend('/system/integrations', {
      method: 'PATCH',
      body,
    });
    if (result?.status !== 'ok') {
      useAlert(result?.message || t('OVVA_INTEGRATIONS_MGMT.API.SAVE_ERROR'));
      return;
    }
    const parts = [t('OVVA_INTEGRATIONS_MGMT.API.SAVE_SUCCESS')];
    if (result.telegram) {
      parts.push(
        result.telegram.ok
          ? t('OVVA_INTEGRATIONS_MGMT.API.TELEGRAM_RECONNECTED')
          : t('OVVA_INTEGRATIONS_MGMT.API.TELEGRAM_FAILED', {
              message: result.telegram.message,
            })
      );
    }
    useAlert(parts.join(' '));
    await fetchIntegrations();
  } catch (error) {
    if (isAuthRejected(error)) {
      resetToGate(error.message);
    } else {
      useAlert(error.message || t('OVVA_INTEGRATIONS_MGMT.API.SAVE_ERROR'));
    }
  } finally {
    isSaving.value = false;
  }
};

const mailSettingsUrl = () =>
  `/api/v1/accounts/${accountId.value}/ovva_mail_settings`;

const applyMailSettings = data => {
  mailCurrent.value = data;
  Object.entries(MAIL_PLAIN_FIELDS).forEach(([key, apiKey]) => {
    mailForm[key] = data?.[apiKey] ? String(data[apiKey]) : '';
  });
  mailForm.smtpPassword = '';
};

const fetchMailSettings = async () => {
  isFetchingMail.value = true;
  try {
    const { data } = await axios.get(mailSettingsUrl());
    applyMailSettings(data);
  } catch (error) {
    const message = error?.response?.data?.message;
    if (error?.response?.status === 403) {
      mailDeniedMessage.value =
        message || t('OVVA_INTEGRATIONS_MGMT.SMTP.SUPER_ADMIN_ONLY');
    } else {
      useAlert(message || t('OVVA_INTEGRATIONS_MGMT.SMTP.FETCH_ERROR'));
    }
  } finally {
    isFetchingMail.value = false;
  }
};

const buildMailPatch = () => {
  const body = {};
  Object.entries(MAIL_PLAIN_FIELDS).forEach(([key, apiKey]) => {
    const value = String(mailForm[key]).trim();
    const saved = String(mailCurrent.value?.[apiKey] ?? '');
    if (value && value !== saved) body[apiKey] = value;
  });
  const password = String(mailForm.smtpPassword).trim();
  if (password) body.smtp_password = password;
  return body;
};

const saveMailSettings = async () => {
  const body = buildMailPatch();
  if (!Object.keys(body).length) {
    useAlert(t('OVVA_INTEGRATIONS_MGMT.API.NOTHING_TO_SAVE'));
    return;
  }
  isSavingMail.value = true;
  try {
    const { data } = await axios.patch(mailSettingsUrl(), body);
    applyMailSettings(data);
    useAlert(t('OVVA_INTEGRATIONS_MGMT.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('OVVA_INTEGRATIONS_MGMT.SMTP.SAVE_ERROR')
    );
  } finally {
    isSavingMail.value = false;
  }
};

onBeforeMount(() => {
  if (!showAuthGate.value) fetchIntegrations();
  fetchMailSettings();
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('OVVA_INTEGRATIONS_MGMT.HEADER')"
        :description="$t('OVVA_INTEGRATIONS_MGMT.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col w-full max-w-3xl gap-0">
        <SectionLayout
          v-if="showAuthGate"
          with-border
          :title="$t('OVVA_INTEGRATIONS_MGMT.PAT.TITLE')"
          :description="$t('OVVA_INTEGRATIONS_MGMT.PAT.DESCRIPTION')"
        >
          <p class="max-w-md mb-3 text-body-main text-n-slate-11">
            {{ $t('OVVA_INTEGRATIONS_MGMT.PAT.HINT') }}
          </p>
          <form
            class="flex flex-col max-w-md gap-3"
            @submit.prevent="submitPat"
          >
            <Input
              v-model="patInputValue"
              autocomplete="new-password"
              type="password"
              :label="$t('OVVA_INTEGRATIONS_MGMT.PAT.TOKEN_LABEL')"
              :placeholder="$t('OVVA_INTEGRATIONS_MGMT.PAT.TOKEN_PLACEHOLDER')"
              :message="patError"
              :message-type="patError ? 'error' : 'info'"
            />
            <div>
              <Button
                type="submit"
                size="sm"
                :label="$t('OVVA_INTEGRATIONS_MGMT.PAT.CONNECT')"
              />
            </div>
          </form>
        </SectionLayout>

        <template v-else>
          <woot-loading-state
            v-if="isFetching"
            :message="$t('OVVA_INTEGRATIONS_MGMT.LOADING')"
          />
          <form v-else-if="current" @submit.prevent="saveSettings">
            <SectionLayout
              with-border
              :title="$t('OVVA_INTEGRATIONS_MGMT.CHANNELS.TITLE')"
              :description="$t('OVVA_INTEGRATIONS_MGMT.CHANNELS.DESCRIPTION')"
            >
              <div class="flex flex-col gap-6">
                <div class="flex flex-col gap-3">
                  <span class="text-heading-3 text-n-slate-12">
                    {{ $t('OVVA_INTEGRATIONS_MGMT.CHANNELS.WHATSAPP') }}
                  </span>
                  <Input
                    v-model="form.wazzupApiKey"
                    autocomplete="new-password"
                    type="password"
                    :label="$t('OVVA_INTEGRATIONS_MGMT.CHANNELS.WHATSAPP_KEY')"
                    :placeholder="maskFor('wazzupApiKey')"
                  />
                  <Input
                    v-model="form.wazzupChannelId"
                    autocomplete="off"
                    :label="
                      $t('OVVA_INTEGRATIONS_MGMT.CHANNELS.WHATSAPP_CHANNEL')
                    "
                    :placeholder="
                      $t(
                        'OVVA_INTEGRATIONS_MGMT.CHANNELS.WHATSAPP_CHANNEL_PLACEHOLDER'
                      )
                    "
                  />
                </div>

                <div class="flex flex-col gap-3">
                  <span class="text-heading-3 text-n-slate-12">
                    {{ $t('OVVA_INTEGRATIONS_MGMT.CHANNELS.TELEGRAM') }}
                  </span>
                  <Input
                    v-model="form.telegramBotToken"
                    autocomplete="new-password"
                    type="password"
                    :label="
                      $t('OVVA_INTEGRATIONS_MGMT.CHANNELS.TELEGRAM_TOKEN')
                    "
                    :placeholder="maskFor('telegramBotToken')"
                  />
                  <p class="m-0 text-label-small text-n-slate-11">
                    {{ $t('OVVA_INTEGRATIONS_MGMT.CHANNELS.TELEGRAM_HINT') }}
                  </p>
                </div>

                <div class="flex flex-col gap-3">
                  <span class="text-heading-3 text-n-slate-12">
                    {{ $t('OVVA_INTEGRATIONS_MGMT.CHANNELS.MAX') }}
                  </span>
                  <Input
                    v-model="form.maxApiKey"
                    autocomplete="new-password"
                    type="password"
                    :label="$t('OVVA_INTEGRATIONS_MGMT.CHANNELS.MAX_KEY')"
                    :placeholder="maskFor('maxApiKey')"
                  />
                </div>
              </div>
            </SectionLayout>

            <div class="flex items-center gap-3 pb-8">
              <Button
                type="submit"
                size="sm"
                :label="$t('OVVA_INTEGRATIONS_MGMT.SAVE')"
                :is-loading="isSaving"
              />
              <span
                class="text-label-small text-n-slate-11 ltr:ml-auto rtl:mr-auto"
              >
                {{ $t('OVVA_INTEGRATIONS_MGMT.PAT.CONNECTED') }}
                <button
                  type="button"
                  class="text-n-blue-11 hover:underline"
                  @click="disconnectPat"
                >
                  {{ $t('OVVA_INTEGRATIONS_MGMT.PAT.DISCONNECT') }}
                </button>
              </span>
            </div>
          </form>
        </template>

        <SectionLayout
          with-border
          :title="$t('OVVA_INTEGRATIONS_MGMT.SMTP.TITLE')"
          :description="$t('OVVA_INTEGRATIONS_MGMT.SMTP.DESCRIPTION')"
        >
          <p
            v-if="mailDeniedMessage"
            class="m-0 text-body-main text-n-slate-11"
          >
            {{ mailDeniedMessage }}
          </p>
          <woot-loading-state
            v-else-if="isFetchingMail"
            :message="$t('OVVA_INTEGRATIONS_MGMT.LOADING')"
          />
          <form
            v-else-if="mailCurrent"
            class="flex flex-col gap-4"
            @submit.prevent="saveMailSettings"
          >
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-2">
                <Input
                  v-model="mailForm.smtpAddress"
                  autocomplete="off"
                  :label="$t('OVVA_INTEGRATIONS_MGMT.SMTP.SERVER')"
                  :placeholder="
                    $t('OVVA_INTEGRATIONS_MGMT.SMTP.SERVER_PLACEHOLDER')
                  "
                />
              </div>
              <Input
                v-model="mailForm.smtpPort"
                autocomplete="off"
                :label="$t('OVVA_INTEGRATIONS_MGMT.SMTP.PORT')"
                :placeholder="
                  $t('OVVA_INTEGRATIONS_MGMT.SMTP.PORT_PLACEHOLDER')
                "
              />
            </div>
            <Input
              v-model="mailForm.smtpUsername"
              autocomplete="off"
              :label="$t('OVVA_INTEGRATIONS_MGMT.SMTP.USERNAME')"
              :placeholder="
                $t('OVVA_INTEGRATIONS_MGMT.SMTP.USERNAME_PLACEHOLDER')
              "
            />
            <Input
              v-model="mailForm.smtpPassword"
              autocomplete="new-password"
              type="password"
              :label="$t('OVVA_INTEGRATIONS_MGMT.SMTP.PASSWORD')"
              :placeholder="mailCurrent.smtp_password"
            />
            <Input
              v-model="mailForm.smtpSenderEmail"
              autocomplete="off"
              :label="$t('OVVA_INTEGRATIONS_MGMT.SMTP.SENDER')"
              :placeholder="SENDER_PLACEHOLDER"
            />
            <div>
              <Button
                type="submit"
                size="sm"
                :label="$t('OVVA_INTEGRATIONS_MGMT.SAVE')"
                :is-loading="isSavingMail"
              />
            </div>
          </form>
        </SectionLayout>
      </div>
    </template>
  </SettingsLayout>
</template>
